import * as Toast from "@radix-ui/react-toast";
import React, { useEffect, useState } from "react";
import { RegionSelector } from "../region/RegionSelector";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Leisure } from "../form-property/leisure";
import { LeisureData, Region } from "../form-property/property-types";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

interface CondominiumAttributes {
  Name?: string;
  region?: string;
  street?: string;
  neighborhood?: string;
  number?: string;
  zipCode?: string;
  commercialStatus?: string;
  builderName?: string;
  developerName?: string;
  administratorName?: string;
  managerName?: string;
  managerPhone?: string;
  caretakerName?: string;
  caretakerPhone?: string;
  constructionYear?: string;
  numberOfServiceElevators?: string;
  numberOfPassengerElevators?: string;
  deliverySchedule?: string;
  corner?: boolean;
  guardhouse?: boolean;
  intercom?: boolean;
  terrace?: boolean;
  undergroundGarage?: boolean;
  beachService?: boolean;
  beachEquipmentStorage?: boolean;
  electronicGate?: boolean;
  groundFloorGarage?: boolean;
  collectiveGas?: boolean;
}

interface CondominiumForDialog {
  id: number;
  attributes: CondominiumAttributes;
}

interface CreateCondominiumDialogProps {
  onCondominiumCreate: (condominium: any) => void;
  trigger?: React.ReactNode;
  existingCondominium?: CondominiumForDialog;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateCondominiumDialog({
  onCondominiumCreate,
  trigger,
  existingCondominium,
  open,
  onOpenChange,
}: CreateCondominiumDialogProps) {
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [street, setStreet] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [number, setNumber] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [commercialStatus, setCommercialStatus] = useState("");
  const [builderName, setBuilderName] = useState("");
  const [developerName, setDeveloperName] = useState("");
  const [managerName, setManagerName] = useState("");
  const [managerPhone, setManagerPhone] = useState("");
  const [caretakerName, setCaretakerName] = useState("");
  const [caretakerPhone, setCaretakerPhone] = useState("");
  const [constructionYear, setConstructionYear] = useState("");
  const [administratorName, setAdministratorName] = useState("");
  const [numberOfServiceElevators, setNumberOfServiceElevators] = useState("");
  const [numberOfPassengerElevators, setNumberOfPassengerElevators] =
    useState("");
  const [deliverySchedule, setDeliverySchedule] = useState("");
  const [corner, setCorner] = useState(false);
  const [guardhouse, setGuardhouse] = useState(false);
  const [intercom, setIntercom] = useState(false);
  const [terrace, setTerrace] = useState(false);
  const [undergroundGarage, setUndergroundGarage] = useState(false);
  const [beachService, setBeachService] = useState(false);
  const [beachEquipmentStorage, setBeachEquipmentStorage] = useState(false);
  const [electronicGate, setElectronicGate] = useState(false);
  const [groundFloorGarage, setGroundFloorGarage] = useState(false);
  const [collectiveGas, setCollectiveGas] = useState(false);
  const [leisureData, setLeisureData] = useState<LeisureData>({
    id: 0,
    partyHall: false,
    adultPool: false,
    gameRoom: false,
    kidsPool: false,
    gym: false,
    playground: false,
    sportsCourt: false,
    sauna: false,
    tennisCourt: false,
    bbqArea: false,
    squashCourt: false,
    socialHall: false,
    wetDeckPool: false,
    lapPool: false,
    kidsBeachPool: false,
    solarium: false,
    poolBar: false,
    massageRoom: false,
    womenSpace: false,
    petPlace: false,
    youthGameRoom: false,
    toyLibrary: false,
    pilatesSpace: false,
    lounge: false,
    gourmetSpace: false,
    kidsPartyRoom: false,
    adultPartyRoomWithBar: false,
    adultGameRoom: false,
    readingRoom: false,
    hammockArea: false,
    zenSpace: false,
    coworkingSpace: false,
    adultTVRoom: false,
    gourmetBar: false,
    cinemaRoom: false,
    sacada: false,
    property: { data: null },
    photos: { connect: [] },
    uploadPhotos: [],
    linkPhotos: "",
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [commercialStatusOptions, setCommercialStatusOptions] = useState<
    string[]
  >([]);

  const handleRegionSelect = (regionObj: Region) => {
    setRegion(regionObj.attributes.Name);
  };

  const fetchCondominiumLeisure = async (condominiumId: number) => {
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(
        `${route}/api/condominiums/${condominiumId}?populate=leisure.photos`,
      );
      const data = await response.json();

      if (data.data.attributes.leisure?.data) {
        const leisureData = data.data.attributes.leisure.data;
        setLeisureData({
          id: leisureData.id,
          ...leisureData.attributes,
          property: { data: null },
          photos: { connect: [] },
          uploadPhotos: [],
          linkPhotos: leisureData.attributes.linkPhotos || "",
        });
      }
    } catch (error) {
      console.error("Erro ao buscar lazer do condomínio:", error);
    }
  };
  useEffect(() => {
    setCommercialStatusOptions(["Disponível", "Vendido", "Pendente"]);
  }, []);

  useEffect(() => {
    const fetchAddress = async () => {
      const sanitized = zipCode.replace(/\D/g, "");
      if (sanitized.length === 8) {
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${sanitized}/json/`,
          );
          const data = await response.json();
          if (!data.erro) {
            setStreet(data.logradouro || "");
            setNeighborhood(data.bairro || "");
          } else {
            setToastType("error");
            setToastMessage("CEP não encontrado.");
            setToastOpen(true);
          }
        } catch (error) {
          console.error("Erro ao buscar o endereço:", error);
          setToastType("error");
          setToastMessage("Erro ao buscar o endereço.");
          setToastOpen(true);
        }
      }
    };
    if (zipCode) {
      fetchAddress();
    }
  }, [zipCode]);

  useEffect(() => {
    if (existingCondominium) {
      const a = existingCondominium.attributes;
      setName(a.Name || "");
      setRegion(a.region || "");
      setStreet(a.street || "");
      setNeighborhood(a.neighborhood || "");
      setNumber(a.number || "");
      setZipCode(a.zipCode || "");
      setCommercialStatus(a.commercialStatus || "");
      setBuilderName(a.builderName || "");
      setDeveloperName(a.developerName || "");
      setManagerName(a.managerName || "");
      setManagerPhone(a.managerPhone || "");
      setCaretakerName(a.caretakerName || "");
      setCaretakerPhone(a.caretakerPhone || "");
      setConstructionYear(a.constructionYear || "");
      setAdministratorName(a.administratorName || "");
      setNumberOfServiceElevators(a.numberOfServiceElevators || "");
      setNumberOfPassengerElevators(a.numberOfPassengerElevators || "");
      setDeliverySchedule(a.deliverySchedule || "");
      setCorner(a.corner || false);
      setGuardhouse(a.guardhouse || false);
      setIntercom(a.intercom || false);
      setTerrace(a.terrace || false);
      setUndergroundGarage(a.undergroundGarage || false);
      setBeachService(a.beachService || false);
      setBeachEquipmentStorage(a.beachEquipmentStorage || false);
      setElectronicGate(a.electronicGate || false);
      setGroundFloorGarage(a.groundFloorGarage || false);
      setCollectiveGas(a.collectiveGas || false);

      if (existingCondominium.id) {
        fetchCondominiumLeisure(existingCondominium.id);
      }
    } else {
      setName("");
      setRegion("");
      setStreet("");
      setNeighborhood("");
      setNumber("");
      setZipCode("");
      setCommercialStatus("");
      setBuilderName("");
      setDeveloperName("");
      setManagerName("");
      setManagerPhone("");
      setCaretakerName("");
      setCaretakerPhone("");
      setConstructionYear("");
      setAdministratorName("");
      setNumberOfServiceElevators("");
      setNumberOfPassengerElevators("");
      setDeliverySchedule("");
      setCorner(false);
      setGuardhouse(false);
      setIntercom(false);
      setTerrace(false);
      setUndergroundGarage(false);
      setBeachService(false);
      setBeachEquipmentStorage(false);
      setElectronicGate(false);
      setGroundFloorGarage(false);
      setCollectiveGas(false);
      setLeisureData({
        id: 0,
        partyHall: false,
        adultPool: false,
        gameRoom: false,
        kidsPool: false,
        gym: false,
        playground: false,
        sportsCourt: false,
        sauna: false,
        tennisCourt: false,
        bbqArea: false,
        squashCourt: false,
        socialHall: false,
        wetDeckPool: false,
        lapPool: false,
        kidsBeachPool: false,
        solarium: false,
        poolBar: false,
        massageRoom: false,
        womenSpace: false,
        petPlace: false,
        youthGameRoom: false,
        toyLibrary: false,
        pilatesSpace: false,
        lounge: false,
        gourmetSpace: false,
        kidsPartyRoom: false,
        adultPartyRoomWithBar: false,
        adultGameRoom: false,
        readingRoom: false,
        hammockArea: false,
        zenSpace: false,
        coworkingSpace: false,
        adultTVRoom: false,
        gourmetBar: false,
        cinemaRoom: false,
        property: { data: null },
        photos: { connect: [] },
        uploadPhotos: [],
        linkPhotos: "",
      });
    }
  }, [existingCondominium]);

  const handleLeisureCheckboxChange = (
    name: keyof LeisureData,
    checked: boolean | "indeterminate",
  ) => {
    setLeisureData((p) => ({ ...p, [name]: checked === true }));
  };

  const handleLeisureChange = (field: keyof LeisureData, value: unknown) => {
    setLeisureData((p) => ({ ...p, [field]: value }));
  };

  async function handleCreateCondominium() {
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      if (!existingCondominium) {
        const checkResp = await fetch(
          `${route}/api/condominiums?filters[Name][$eq]=${encodeURIComponent(
            name,
          )}`,
        );
        const checkJson = await checkResp.json();
        if (Array.isArray(checkJson.data) && checkJson.data.length > 0) {
          setToastType("error");
          setToastMessage("Já existe um condomínio com este nome!");
          setToastOpen(true);
          return;
        }
      }
      let leisureId = null;

      if (existingCondominium && leisureData.id > 0) {
        let photoIds: number[] = [];
        if (leisureData.uploadPhotos && leisureData.uploadPhotos.length > 0) {
          const fData = new FormData();
          leisureData.uploadPhotos.forEach((file) => {
            if (file instanceof File) {
              fData.append("files", file);
            }
          });
          const uploadResp = await fetch(`${route}/api/upload`, {
            method: "POST",
            body: fData,
          });
          if (!uploadResp.ok) {
            const upErr = await uploadResp.json();
            console.error("Erro no upload das fotos:", upErr);
            throw new Error("Erro ao fazer upload das fotos do Lazer.");
          }
          const upResult = await uploadResp.json();
          if (Array.isArray(upResult)) {
            photoIds = upResult.map((p: { id: number }) => p.id);
          } else {
            console.error("Resposta de upload inesperada:", upResult);
            throw new Error("Formato inesperado no upload de fotos.");
          }
        }

        const leisurePayload = {
          data: {
            partyHall: leisureData.partyHall,
            adultPool: leisureData.adultPool,
            gameRoom: leisureData.gameRoom,
            kidsPool: leisureData.kidsPool,
            gym: leisureData.gym,
            playground: leisureData.playground,
            sportsCourt: leisureData.sportsCourt,
            sauna: leisureData.sauna,
            tennisCourt: leisureData.tennisCourt,
            bbqArea: leisureData.bbqArea,
            squashCourt: leisureData.squashCourt,
            socialHall: leisureData.socialHall,
            wetDeckPool: leisureData.wetDeckPool,
            lapPool: leisureData.lapPool,
            kidsBeachPool: leisureData.kidsBeachPool,
            solarium: leisureData.solarium,
            poolBar: leisureData.poolBar,
            massageRoom: leisureData.massageRoom,
            womenSpace: leisureData.womenSpace,
            petPlace: leisureData.petPlace,
            youthGameRoom: leisureData.youthGameRoom,
            toyLibrary: leisureData.toyLibrary,
            pilatesSpace: leisureData.pilatesSpace,
            lounge: leisureData.lounge,
            gourmetSpace: leisureData.gourmetSpace,
            kidsPartyRoom: leisureData.kidsPartyRoom,
            adultPartyRoomWithBar: leisureData.adultPartyRoomWithBar,
            adultGameRoom: leisureData.adultGameRoom,
            readingRoom: leisureData.readingRoom,
            hammockArea: leisureData.hammockArea,
            zenSpace: leisureData.zenSpace,
            coworkingSpace: leisureData.coworkingSpace,
            adultTVRoom: leisureData.adultTVRoom,
            gourmetBar: leisureData.gourmetBar,
            cinemaRoom: leisureData.cinemaRoom,
            sacada: leisureData.sacada,
            linkPhotos: leisureData.linkPhotos,
            ...(photoIds.length > 0 && { photos: photoIds }),
          },
        };

        const leisureResp = await fetch(
          `${route}/api/leisures/${leisureData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(leisurePayload),
          },
        );

        if (!leisureResp.ok) {
          const leisureError = await leisureResp.json();
          console.error("Erro ao atualizar Lazer:", leisureError);
          throw new Error("Erro ao atualizar Lazer.");
        }

        leisureId = leisureData.id;
      } else if (!existingCondominium) {
        let photoIds: number[] = [];
        if (leisureData.uploadPhotos && leisureData.uploadPhotos.length > 0) {
          const fData = new FormData();
          leisureData.uploadPhotos.forEach((file) => {
            if (file instanceof File) {
              fData.append("files", file);
            }
          });
          const uploadResp = await fetch(`${route}/api/upload`, {
            method: "POST",
            body: fData,
          });
          if (!uploadResp.ok) {
            const upErr = await uploadResp.json();
            console.error("Erro no upload das fotos:", upErr);
            throw new Error("Erro ao fazer upload das fotos do Lazer.");
          }
          const upResult = await uploadResp.json();
          if (Array.isArray(upResult)) {
            photoIds = upResult.map((p: { id: number }) => p.id);
          } else {
            console.error("Resposta de upload inesperada:", upResult);
            throw new Error("Formato inesperado no upload de fotos.");
          }
        }
        const leisurePayload = {
          data: {
            partyHall: leisureData.partyHall,
            adultPool: leisureData.adultPool,
            gameRoom: leisureData.gameRoom,
            kidsPool: leisureData.kidsPool,
            gym: leisureData.gym,
            playground: leisureData.playground,
            sportsCourt: leisureData.sportsCourt,
            sauna: leisureData.sauna,
            tennisCourt: leisureData.tennisCourt,
            bbqArea: leisureData.bbqArea,
            squashCourt: leisureData.squashCourt,
            socialHall: leisureData.socialHall,
            wetDeckPool: leisureData.wetDeckPool,
            lapPool: leisureData.lapPool,
            kidsBeachPool: leisureData.kidsBeachPool,
            solarium: leisureData.solarium,
            poolBar: leisureData.poolBar,
            massageRoom: leisureData.massageRoom,
            womenSpace: leisureData.womenSpace,
            petPlace: leisureData.petPlace,
            youthGameRoom: leisureData.youthGameRoom,
            toyLibrary: leisureData.toyLibrary,
            pilatesSpace: leisureData.pilatesSpace,
            lounge: leisureData.lounge,
            gourmetSpace: leisureData.gourmetSpace,
            kidsPartyRoom: leisureData.kidsPartyRoom,
            adultPartyRoomWithBar: leisureData.adultPartyRoomWithBar,
            adultGameRoom: leisureData.adultGameRoom,
            readingRoom: leisureData.readingRoom,
            hammockArea: leisureData.hammockArea,
            zenSpace: leisureData.zenSpace,
            coworkingSpace: leisureData.coworkingSpace,
            adultTVRoom: leisureData.adultTVRoom,
            gourmetBar: leisureData.gourmetBar,
            cinemaRoom: leisureData.cinemaRoom,
            sacada: leisureData.sacada,
            linkPhotos: leisureData.linkPhotos,
            photos: photoIds,
          },
        };
        const leisureResp = await fetch(`${route}/api/leisures`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(leisurePayload),
        });
        if (!leisureResp.ok) {
          const leisureError = await leisureResp.json();
          console.error("Erro ao criar Lazer:", leisureError);
          throw new Error("Erro ao cadastrar Lazer.");
        }
        const leisureJson = await leisureResp.json();
        leisureId = leisureJson.data.id;
      }

      const condPayload = {
        data: {
          Name: name,
          region,
          street,
          neighborhood,
          number,
          zipCode,
          commercialStatus,
          builderName,
          developerName,
          managerName,
          managerPhone,
          caretakerName,
          caretakerPhone,
          constructionYear,
          administratorName,
          numberOfServiceElevators,
          numberOfPassengerElevators,
          deliverySchedule,
          corner,
          guardhouse,
          intercom,
          terrace,
          undergroundGarage,
          beachService,
          beachEquipmentStorage,
          electronicGate,
          groundFloorGarage,
          collectiveGas,
          leisure: leisureId || undefined,
        },
      };

      const method = existingCondominium ? "PUT" : "POST";
      const url = existingCondominium
        ? `${route}/api/condominiums/${existingCondominium.id}`
        : `${route}/api/condominiums`;
      const resp = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(condPayload),
      });
      if (!resp.ok) {
        const errData = await resp.json();
        const msg = errData?.message || "Falha ao salvar o condomínio";
        throw new Error(msg);
      }
      const newData = await resp.json();
      const savedCondominium = newData.data;
      onCondominiumCreate(savedCondominium);

      // Definir toast de sucesso ANTES de fechar o modal
      setToastType("success");
      setToastMessage(
        existingCondominium
          ? "Condomínio atualizado com sucesso!"
          : "Condomínio criado com sucesso!",
      );
      setToastOpen(true);

      // Aguardar um pouco antes de fechar o modal para garantir que o toast apareça
      setTimeout(() => {
        onOpenChange && onOpenChange(false);
      }, 100);
    } catch (error) {
      console.error("Failed to save condominium:", error);
      setToastType("error");
      setToastMessage(
        error instanceof Error
          ? error.message
          : "Falha ao salvar o condomínio!",
      );
      setToastOpen(true);
    }
  }

  return (
    <div>
      <Toast.Provider swipeDirection="right">
        <Dialog open={open} onOpenChange={onOpenChange}>
          {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
          <DialogContent className="max-h-[90vh] overflow-y-auto rounded-md bg-white p-6 shadow-lg sm:max-w-[800px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {existingCondominium ? "Editar Condomínio" : "Criar Condomínio"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Preencha as informações e clique em salvar
              </DialogDescription>
            </DialogHeader>

            <Accordion type="multiple" className="w-full">
              <AccordionItem value="basicInfo">
                <AccordionTrigger className="text-lg font-medium">
                  Dados Básicos
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="name" className="text-right">
                        Nome
                      </Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="commercialStatus" className="text-right">
                        Status Comercial
                      </Label>
                      <Select
                        value={commercialStatus}
                        onValueChange={(val) => setCommercialStatus(val)}
                      >
                        <SelectTrigger className="col-span-3">
                          <SelectValue placeholder="Selecione o Status Comercial" />
                        </SelectTrigger>
                        <SelectContent>
                          {commercialStatusOptions.map((st) => (
                            <SelectItem key={st} value={st}>
                              {st}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="address">
                <AccordionTrigger className="text-lg font-medium">
                  Endereço
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="zipCode" className="text-right">
                        CEP
                      </Label>
                      <Input
                        id="zipCode"
                        value={zipCode}
                        onChange={(e) => {
                          const c = e.target.value.replace(/\D/g, "");
                          setZipCode(c);
                        }}
                        className="col-span-3"
                        maxLength={8}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="street" className="text-right">
                        Rua
                      </Label>
                      <Input
                        id="street"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="number" className="text-right">
                        Número
                      </Label>
                      <Input
                        id="number"
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="neighborhood" className="text-right">
                        Bairro
                      </Label>
                      <Input
                        id="neighborhood"
                        value={neighborhood}
                        onChange={(e) => setNeighborhood(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label className="text-right"></Label>
                      <div className="col-span-3">
                        <RegionSelector
                          onSelect={handleRegionSelect}
                          selectedValue={region}
                          required={true}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="condoInfo">
                <AccordionTrigger className="text-lg font-medium">
                  Informações do Condomínio
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="builderName" className="text-right">
                        Nome da Construtora
                      </Label>
                      <Input
                        id="builderName"
                        value={builderName}
                        onChange={(e) => setBuilderName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="developerName" className="text-right">
                        Nome da Incorporadora
                      </Label>
                      <Input
                        id="developerName"
                        value={developerName}
                        onChange={(e) => setDeveloperName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="administratorName" className="text-right">
                        Nome da Administradora
                      </Label>
                      <Input
                        id="administratorName"
                        value={administratorName}
                        onChange={(e) => setAdministratorName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="constructionYear" className="text-right">
                        Ano de Construção
                      </Label>
                      <Input
                        id="constructionYear"
                        value={constructionYear}
                        onChange={(e) => setConstructionYear(e.target.value)}
                        className="col-span-3"
                        type="number"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="deliverySchedule" className="text-right">
                        Previsão de Entrega
                      </Label>
                      <Input
                        id="deliverySchedule"
                        value={deliverySchedule}
                        onChange={(e) => setDeliverySchedule(e.target.value)}
                        className="col-span-3"
                        type="date"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="contacts">
                <AccordionTrigger className="text-lg font-medium">
                  Contatos
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="managerName" className="text-right">
                        Nome do Sindico
                      </Label>
                      <Input
                        id="managerName"
                        value={managerName}
                        onChange={(e) => setManagerName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="managerPhone" className="text-right">
                        Telefone do Sindico
                      </Label>
                      <Input
                        id="managerPhone"
                        value={managerPhone}
                        onChange={(e) => setManagerPhone(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="caretakerName" className="text-right">
                        Nome do Zelador
                      </Label>
                      <Input
                        id="caretakerName"
                        value={caretakerName}
                        onChange={(e) => setCaretakerName(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="caretakerPhone" className="text-right">
                        Telefone do Zelador
                      </Label>
                      <Input
                        id="caretakerPhone"
                        value={caretakerPhone}
                        onChange={(e) => setCaretakerPhone(e.target.value)}
                        className="col-span-3"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="features">
                <AccordionTrigger className="text-lg font-medium">
                  Características
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-6 py-4">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div className="flex flex-col">
                        <Label
                          htmlFor="numberOfServiceElevators"
                          className="mb-1 font-semibold text-gray-700"
                        >
                          Nº de Elevadores de Serviço
                        </Label>
                        <Input
                          id="numberOfServiceElevators"
                          value={numberOfServiceElevators}
                          onChange={(e) =>
                            setNumberOfServiceElevators(e.target.value)
                          }
                          type="number"
                        />
                      </div>

                      <div className="flex flex-col">
                        <Label
                          htmlFor="numberOfPassengerElevators"
                          className="mb-1 font-semibold text-gray-700"
                        >
                          Nº de Elevadores de Passageiros
                        </Label>
                        <Input
                          id="numberOfPassengerElevators"
                          value={numberOfPassengerElevators}
                          onChange={(e) =>
                            setNumberOfPassengerElevators(e.target.value)
                          }
                          type="number"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="corner"
                          checked={corner}
                          onCheckedChange={(ch) => setCorner(ch === true)}
                        />
                        <Label
                          htmlFor="corner"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Esquina
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="guardhouse"
                          checked={guardhouse}
                          onCheckedChange={(ch) => setGuardhouse(ch === true)}
                        />
                        <Label
                          htmlFor="guardhouse"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Guarita
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="intercom"
                          checked={intercom}
                          onCheckedChange={(ch) => setIntercom(ch === true)}
                        />
                        <Label
                          htmlFor="intercom"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Interfone
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="terrace"
                          checked={terrace}
                          onCheckedChange={(ch) => setTerrace(ch === true)}
                        />
                        <Label
                          htmlFor="terrace"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Terraço
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="undergroundGarage"
                          checked={undergroundGarage}
                          onCheckedChange={(ch) =>
                            setUndergroundGarage(ch === true)
                          }
                        />
                        <Label
                          htmlFor="undergroundGarage"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Garagem no Subsolo
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="beachService"
                          checked={beachService}
                          onCheckedChange={(ch) => setBeachService(ch === true)}
                        />
                        <Label
                          htmlFor="beachService"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Serviço de Praia
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="beachEquipmentStorage"
                          checked={beachEquipmentStorage}
                          onCheckedChange={(ch) =>
                            setBeachEquipmentStorage(ch === true)
                          }
                        />
                        <Label
                          htmlFor="beachEquipmentStorage"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Depósito de Praia
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="electronicGate"
                          checked={electronicGate}
                          onCheckedChange={(ch) =>
                            setElectronicGate(ch === true)
                          }
                        />
                        <Label
                          htmlFor="electronicGate"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Portão Eletrônico
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="groundFloorGarage"
                          checked={groundFloorGarage}
                          onCheckedChange={(ch) =>
                            setGroundFloorGarage(ch === true)
                          }
                        />
                        <Label
                          htmlFor="groundFloorGarage"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Garagem no Térreo
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="collectiveGas"
                          checked={collectiveGas}
                          onCheckedChange={(ch) =>
                            setCollectiveGas(ch === true)
                          }
                        />
                        <Label
                          htmlFor="collectiveGas"
                          className="cursor-pointer font-medium text-gray-700"
                        >
                          Gás Encanado
                        </Label>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="leisureInfo">
                <AccordionTrigger className="text-lg font-medium">
                  Lazer {existingCondominium ? "(Editar)" : ""}
                </AccordionTrigger>
                <AccordionContent>
                  <Leisure
                    data={leisureData}
                    onChange={handleLeisureChange}
                    onCheckboxChange={handleLeisureCheckboxChange}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DialogFooter>
              <Button type="button" onClick={handleCreateCondominium}>
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={5000}
          className={`fixed bottom-4 right-4 z-50 rounded-md border p-4 shadow-lg ${
            toastType === "success"
              ? "border-green-600 bg-green-500 text-white"
              : "border-red-600 bg-red-500 text-white"
          }`}
        >
          <Toast.Title className="font-semibold">
            {toastType === "success" ? "Sucesso" : "Erro"}
          </Toast.Title>
          <Toast.Description className="mt-1">{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-2 top-2 text-white hover:text-gray-200">
            ✕
          </Toast.Close>
        </Toast.Root>
        <Toast.Viewport className="fixed bottom-0 right-0 z-50 m-0 flex max-h-screen w-96 max-w-[100vw] list-none flex-col gap-2 p-6" />
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
